import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import GlobalContext from "~/GlobalContextProvider";
import useOnlineStatus from "./useOnlineStatus";
import { db } from "~/db/db";
import { useNavigate, useParams } from "react-router-dom";
import useLocalStorage from "./useLocalStorage";
import useCheckIn from "~fetchs/Roterizacao/useCheckIn";


const useCacheMutation = (
  fn,
  key,
  closeModalCallbackOnSuccess,
  closeModalCallbackOnError,
  customMessage,
  customOnSuccess,
  customOnError
) => {
  const parseBackendError = (error) => {
    const responseData = error?.response?.data ?? error?.data ?? error;

    if (!responseData) {
      return {
        message: error?.message ?? "Houve um erro ao processar sua solicitacao, por favor tente novamente",
        submessage: "",
      };
    }

    if (typeof responseData === "string") {
      return {
        message: responseData,
        submessage: "",
      };
    }

    let details = [];

    if (Array.isArray(responseData?.errors)) {
      details = responseData.errors
        .map((entry) => (typeof entry === "string" ? entry : entry?.message))
        .filter(Boolean);
    } else if (responseData?.errors && typeof responseData.errors === "object") {
      details = Object.values(responseData.errors)
        .flat()
        .filter(Boolean);
    } else if (Array.isArray(responseData?.details)) {
      details = responseData.details.filter(Boolean);
    } else if (typeof responseData?.detail === "string") {
      details = [responseData.detail];
    }

    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.title ||
      details[0] ||
      error?.message ||
      "Houve um erro ao processar sua solicitacao, por favor tente novamente";

    return {
      message,
      submessage: details.length > 1 ? details.slice(1).join("; ") : "",
    };
  };
  const isOnline = useOnlineStatus();
  const { setContextData } = useContext(GlobalContext);
  const { parameter: idCliente } = useParams();
  const { mutate } = useMutation({
    mutationKey: key,
    mutationFn: fn,
  });
  const [_, setLocalStorage] = useLocalStorage("validation", null);
  const navigate = useNavigate();
  const { checkin } = useCheckIn();
  const checkRouteRoteirizacao = (idCliente, idClienteRota) => {
    if(idClienteRota !== null || idClienteRota !== undefined){
      if(idCliente === idClienteRota){
        setLocalStorage(idCliente);
        navigate(`/roteirizacao/${idCliente}`);
      }else{
        return null;
      }
    }
  }

  const mutateCached = async (props) => {
    mutate(props, {
      onSuccess,
      onError,
    });

    if (!isOnline) {
      const data = {
        data: props,
        isSuccess: true,
      };

      if (customOnSuccess && typeof customOnSuccess == "function") customOnSuccess(data);
      else {
        setContextData({
          isOpen: true,
          status: "warning",
          message: "Sua solicitação foi processada com sucesso no modo offline",
          submessage: (
            <div className="mt-4">
              <b>Atenção! Você está offline</b>
              <br />
              As alterações realizadas irão propagar para os demais usuários <br />
              assim que o dispositivo for conectado na internet
            </div>
          ),
          onCloseModal: () => {
            if (closeModalCallbackOnSuccess && typeof closeModalCallbackOnSuccess == "function")
              closeModalCallbackOnSuccess(data);
            if(checkin !== undefined){
              checkRouteRoteirizacao(+idCliente, checkin?.clienteId);
            }
          },
        });
      }

      if (!isOnline && idCliente) {
        try {
          const cliente = await db.cliente.get(parseInt(idCliente));
          if (cliente && cliente.fechado === true) {
            console.log("Valor de 'fechado' é true. Tentando atualizar para false...");
            await db.cliente.update(parseInt(idCliente), { fechado: false });
            console.log("Atualização feita. Valor de 'fechado' foi definido como false.");
          } else {
            console.log("O valor de 'fechado' não era true ou cliente não encontrado.");
          }
        } catch (error) {
          console.error("Erro ao acessar o banco de dados local:", error);
        }
      }
    }
  };

  const onSuccess = (data) => {
    if (data) {
      if (data?.status == 400 || data?.data?.isSuccess === false) {
        onError(data);
        return;
      }

      if (customOnSuccess && typeof customOnSuccess == "function") customOnSuccess(data);
      else {
        setContextData({
          isOpen: true,
          status: "success",
          message: data?.message ?? "Informações salvas com sucesso",
          submessage: customMessage,
          onCloseModal: () => {
            if (closeModalCallbackOnSuccess && typeof closeModalCallbackOnSuccess == "function")
              closeModalCallbackOnSuccess(data);
          },
        });
      }
    } else onError(data);
  };

  const onError = (error) => {
    if (customOnError && typeof customOnError == "function") customOnError(error);
    else {
      const { message, submessage } = parseBackendError(error);
      setContextData({
        isOpen: true,
        status: "alert",
        message,
        submessage,
        onCloseModal: () => {
          if (closeModalCallbackOnError && typeof closeModalCallbackOnError == "function")
            closeModalCallbackOnError(error);
        },
      });
    }
  };

  return {
    mutateCached,
  };
};

export default useCacheMutation;
